import type {Evaluator} from "@cm/graph-v3"
import {graphNode} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {MeshNode} from "#template/nodes/object/mesh/mesh-node"
import type {MeshNodeDefinition, MeshNodeParameters} from "#template/nodes/object/mesh/types"
import {meshNodeParameters} from "#template/nodes/object/mesh/validation"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {CurveLikeNode, MeshLikeNode} from "#template/nodes/types"
import {curveLike, isCurveLikeNode} from "#template/nodes/object/curve/fundamental"
import {meshLike} from "#template/nodes/object/mesh/fundamental"
import {NodesInput} from "#template/fundamental/input/nodes-input"
import type {OnCompileContext, OnFilterActiveContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import type {AttributeRef} from "#template/geometry-processing/geometry-graph"
import {continuousCurveAttr, GeomBuilderContext, geomToMesh, meshToGeom, Operators, Primitives} from "#template/geometry-processing/geometry-graph"
import type {CurveObjectData, ObjectData} from "#template/interfaces/object-data"
import {isCurveObjectData} from "#template/interfaces/object-data"
import {GenerateMesh} from "#template/runtime-graph/nodes/generate-mesh"
import {orderAndOrientCurves} from "#template/utils/cut-mesh-utils"
import type {MeshNodes} from "@cm/render-nodes"
import {hashObject} from "@cm/utils/hashing"
import {isEvaluatedTemplateNode} from "#template/utils/template-node-utils"
import {visitNone} from "#template/template-node/visit-helpers"

function applyCutToGeometry(
    baseMeshGraph: MeshNodes.Mesh,
    curvePoints: (Float32Array | undefined)[],
    curveSpace: "position" | "uv",
    numUVChannels: number,
    cutUVChannel: number,
): MeshNodes.Mesh {
    const numGroups = curvePoints.length

    const ctx = new GeomBuilderContext()

    if (cutUVChannel >= numUVChannels) {
        throw new Error("Invalid UV channel")
    }

    if (!curvePoints) {
        return baseMeshGraph
    }

    const baseMesh = meshToGeom(ctx, baseMeshGraph, numUVChannels)

    const curveRegions: [AttributeRef, AttributeRef][] = []
    const groupIDs: number[] = []
    for (let groupIdx = 0; groupIdx < numGroups; groupIdx++) {
        const points = curvePoints[groupIdx]
        if (points) {
            let curveUV: AttributeRef
            if (curveSpace === "uv") {
                curveUV = Primitives.curve(ctx, {
                    uv: continuousCurveAttr(points, "float2"),
                }).uv
            } else {
                const baseCurve = Primitives.curve(ctx, {
                    position: continuousCurveAttr(points, "float3"),
                })
                curveUV = Operators.project(baseCurve.position, baseCurve, baseMesh.position, baseMesh).uvs[cutUVChannel].weld(1e-4)
            }
            const curveGroup = curveUV.constInt(groupIdx)
            curveRegions.push([curveUV, curveGroup])
        }
        groupIDs.push(groupIdx)
    }

    const [curveUV, curveGroup] = Operators.merge(...curveRegions)

    const [cutMesh, windingAttrs] = Operators.cut(baseMesh.uvs[cutUVChannel], baseMesh, curveUV, curveGroup, groupIDs)

    let materialID = windingAttrs[0].constInt(0)

    for (let groupIdx = 0; groupIdx < numGroups; groupIdx++) {
        const winding = windingAttrs[groupIdx]
        const cond = winding.gt(Operators.getCutThreshold(winding))
        materialID = cond.select(groupIdx + 1, materialID)
    }
    materialID = materialID.add(0.5).castInt()

    return geomToMesh({...cutMesh, materialID})
}

export type CutMeshParameters = {
    name: string
    mesh: MeshLikeNode | null
    curves: (CurveLikeNode | NodesInput)[]
    uvChannel: number
} & MeshNodeParameters

export type CutMeshConstructorParameters = Omit<CutMeshParameters, "uvChannel" | "visible"> & {uvChannel?: number; visible?: boolean}

const cutMeshDefinition: MeshNodeDefinition<CutMeshParameters, CutMeshConstructorParameters> = {
    nodeClass: "CutMesh",
    validation: {
        parameters: z.extend(
            z.object({
                name: z.string(),
                mesh: z.nullable(meshLike),
                curves: z.array(z.union([curveLike, graphNode(NodesInput)])),
                uvChannel: z._default(z.number(), 0),
            }),
            meshNodeParameters.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class CutMesh extends MeshNode<CutMeshParameters, CutMeshConstructorParameters> {
    public override get definition() {
        return cutMeshDefinition
    }

    public override onFilterActive(_context: OnFilterActiveContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<CutMeshParameters> {
        if (this.parameters.mesh === null || this.parameters.curves.length === 0) return skipped
        return visitNone(this.parameters)
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<CutMeshParameters> {
        const {evaluator} = context
        const {templateContext, activeNodeSet} = evaluator
        const {sceneManager, computeDummyReifiedMeshData} = templateContext
        const {mesh, curves, uvChannel} = this.parameters

        if (mesh === null || curves.length === 0) return skipped

        const scope = evaluator.getScope(this)
        const [baseMeshObjectData, baseMeshObjectDataInvalid] = scope.branch(evaluator.evaluate(mesh))
        const curveData = scope.merge(
            curves.map((curveNode) => {
                if (isCurveLikeNode(curveNode)) {
                    return scope.list([evaluator.evaluate(curveNode)])
                } else {
                    return scope.pureLambda(
                        curveNode.evaluate(scope, evaluator),
                        (evaluatedNodes) => {
                            return evaluatedNodes.map((evaluatedNode) => {
                                if (evaluatedNode.type === "Mesh" || evaluatedNode.type === "Curve") {
                                    const objectData = evaluatedNode.value as ObjectData | null
                                    if (objectData && isCurveObjectData(objectData)) return objectData as CurveObjectData
                                }

                                return null
                            })
                        },
                        "curveData",
                    )
                }
            }),
        )

        const [curveDataValid, curveDataInvalid] = scope.branch(
            scope.pureLambda(
                curveData,
                (curveData) => {
                    const valid = curveData.filter((curveData) => curveData !== null)
                    if (valid.length === 0) return null
                    return valid
                },
                "curveDataValid",
            ),
        )

        const deferredCurveData = scope.waitForFinishedModifyingScene(curveDataValid, sceneManager)

        const transformed = scope.pureLambda(
            scope.tuple(deferredCurveData, baseMeshObjectData, uvChannel, sceneManager),
            ([curveData, baseMeshObjectData, uvChannel, sceneManager]) => {
                const curves = curveData.filter((curve) => curve !== null)
                const sortedCurves = orderAndOrientCurves(baseMeshObjectData, curves, sceneManager, uvChannel)
                const abstract = baseMeshObjectData.completeMeshData.abstract
                const numUVs = baseMeshObjectData.completeMeshData.reified.uvs.length
                const curvePoints = sortedCurves.map((curveData) => curveData?.completeCurveData.reified?.points)
                return {
                    contentHash: hashObject(["cutMesh", abstract.contentHash, numUVs, curvePoints]),
                    displayGeometryGraph: applyCutToGeometry(abstract.displayGeometryGraph, curvePoints, "position", numUVs, uvChannel),
                    renderGeometryGraph: applyCutToGeometry(abstract.renderGeometryGraph, curvePoints, "position", numUVs, uvChannel),
                    displayGeometryGraphResources: abstract.displayGeometryGraphResources,
                }
            },
            "transformedGraphs",
        )

        const {completeMeshData} = scope.node(GenerateMesh, {
            sceneManager,
            computeDummyReifiedMeshData: computeDummyReifiedMeshData && !isEvaluatedTemplateNode(this, activeNodeSet),
            inputMeshData: transformed,
        })

        this.setupMesh(scope, context, completeMeshData, true, scope.phi(curveDataInvalid, baseMeshObjectDataInvalid))

        return visitNone(this.parameters)
    }
}
